<template>
  <v-card class="rounded-lg fill-height d-flex flex-column">
    <v-card-title class="ml-2 pb-2 flex-shrink-0">
      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('card_title.diagnostics_latest') }}
          </div>
        </v-col>
        <template v-if="!removable">
          <v-col
            cols="auto"
            class="ml-auto"
          >
            <v-btn-toggle
              v-model="dateRange"
              color="primary"
              mandatory
              @change="onPeriodChanged"
            >
              <v-btn
                small
                :value="1"
              >
                {{ $t('button.month_one') }}
              </v-btn>
              <v-btn
                small
                :value="3"
              >
                {{ $t('button.month_three') }}
              </v-btn>
              <v-btn
                small
                :value="6"
              >
                {{ $t('button.month_six') }}
              </v-btn>
              <v-btn
                small
                :value="12"
              >
                {{ $t('button.year_one') }}
              </v-btn>
              <v-btn
                small
                :value="60"
              >
                {{ $t('button.year_five') }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col
            cols="auto"
            class="ml-2"
          >
            <v-menu
              v-model="variablesMenuVisible"
              offset-y
              :close-on-content-click="false"
              @keydown.esc="variablesMenuVisible = false"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  rounded
                  text
                  outlined
                  class="text-none"
                  :loading="variables.loading"
                  v-on="on"
                >
                  <v-icon
                    x-small
                    class="mr-2"
                  >
                    far fa-pen
                  </v-icon>
                  {{ $t('button.edit_columns') }}
                  <span class="ml-2 text-caption grey--text">
                    {{ variables.selected.length }}
                  </span>
                </v-btn>
              </template>

              <v-card width="380">
                <v-text-field
                  v-model="variablesSearch"
                  dense
                  outlined
                  hide-details
                  :label="$t('text_input.search.placeholder')"
                  class="pa-2"
                  clearable
                  @click.stop
                />
                <div style="max-height: 300px; overflow-y: auto;">
                  <draggable
                    :list="variables.selected"
                    handle=".handle"
                    :group="{ name: 'diag-latest-vars' }"
                    :animation="100"
                    @end="onVariablesReordered"
                  >
                    <v-list-item
                      v-for="varCode in variables.selected"
                      :key="varCode"
                      @click.stop="removeVariable(varCode)"
                    >
                      <v-list-item-action>
                        <v-checkbox :input-value="true" />
                      </v-list-item-action>
                      <v-list-item-content class="pa-0">
                        <v-list-item-title class="text-truncate">
                          {{ variableLabel(variableByCode[varCode]) || varCode }}
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-icon
                          small
                          class="handle"
                          style="cursor: move"
                        >
                          far fa-grip-lines
                        </v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </draggable>
                </div>
                <v-divider v-if="filteredUnselectedVariables.length" />
                <v-virtual-scroll
                  v-if="filteredUnselectedVariables.length"
                  :items="filteredUnselectedVariables"
                  :item-height="40"
                  :height="Math.min(filteredUnselectedVariables.length * 40, 250)"
                  bench="5"
                >
                  <template v-slot:default="{ item }">
                    <v-list-item
                      :key="item.var_id"
                      @click.stop="addVariable(item.var_id)"
                    >
                      <v-list-item-action>
                        <v-checkbox :value="false" />
                      </v-list-item-action>
                      <v-list-item-content class="pa-0">
                        <v-list-item-title class="text-truncate">
                          {{ variableLabel(item) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-virtual-scroll>
              </v-card>
            </v-menu>
          </v-col>
        </template>
        <v-col
          v-if="removable"
          cols="auto"
          class="ml-auto"
        >
          <v-btn
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pa-0 flex-grow-1">
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="stats.loading"
        hide-default-footer
        :items-per-page="-1"
        dense
        sort-by="last_name"
        :sort-desc="false"
      >
        <template v-slot:item="{ item }">
          <tr
            :style="singlePlayer ? null : { cursor: 'pointer' }"
            @click="onRowClick(item)"
          >
            <td
              v-if="!singlePlayer"
              style="width: 0px"
              class="pr-0"
            >
              <v-avatar
                size="32px"
                class="mr-2"
                color="grey lighten-2"
              >
                <v-img :src="item.photo_url">
                  <template v-slot:placeholder>
                    <v-icon x-small>
                      fal fa-user
                    </v-icon>
                  </template>
                </v-img>
              </v-avatar>
            </td>
            <td
              v-if="!singlePlayer"
              style="max-width: 200px"
            >
              <div class="d-flex align-center">
                <div class="flex-grow-1 font-weight-medium">
                  {{ item.last_name }} {{ item.first_name }}
                </div>

                <div
                  v-if="item.latest_stats && item.latest_stats.injured && canSeeMedicalIndicator(item)"
                  class="d-flex align-center justify-center ml-2"
                  style="width: 24px; min-width: 24px;"
                >
                  <img
                    :src="injuryIcon"
                    style="width: 22px; height: 22px"
                  >
                </div>

                <div
                  v-if="hasSmartAssistantModule"
                  class="d-flex align-center justify-center ml-2"
                  style="width: 24px; min-width: 24px; cursor: pointer;"
                  @click.stop.prevent="navigateToSmartAssistant(item)"
                >
                  <v-tooltip
                    v-if="(item.smart_assistant || []).length"
                    top
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-badge
                          :content="item.smart_assistant.length"
                          color="primary"
                          inline
                          :value="item.smart_assistant.length"
                        />
                      </span>
                    </template>
                    <span>{{ $tc('tooltip.smart_assistant', item.smart_assistant.length, { num: item.smart_assistant.length }) }}</span>
                  </v-tooltip>

                  <v-tooltip
                    v-else
                    top
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon
                          color="success"
                          size="22px"
                        >
                          fas fa-circle-check
                        </v-icon>
                      </span>
                    </template>
                    <span>{{ $t('smart_assistant.no_alerts') }}</span>
                  </v-tooltip>
                </div>
              </div>
            </td>
            <td v-if="!singlePlayer && !isIndividualSport">
              <v-chip
                v-for="position in (item.player_positions || []).slice(0, 3)"
                :key="item.id + '-' + position.id"
                small
                label
                class="mx-1"
              >
                {{ position.short_name }}
              </v-chip>
            </td>
            <td
              v-for="varCode in variables.selected"
              :key="varCode"
              class="text-no-wrap text-caption"
            >
              <template v-if="item.diag_stats[varCode]">
                <span class="font-weight-bold">
                  {{ formatValue(item.diag_stats[varCode].latest) }}{{ unitFor(varCode) }}
                </span>
                <span class="grey--text">
                  / {{ formatValue(item.diag_stats[varCode].previous) }}{{ unitFor(varCode) }}
                </span>
                <v-icon
                  small
                  :color="item.diag_stats[varCode].colour || 'grey'"
                >
                  {{ arrowIcon(item.diag_stats[varCode]) }}
                </v-icon>
                <span
                  v-if="item.diag_stats[varCode].percentage !== null && item.diag_stats[varCode].percentage !== undefined"
                  :style="{ color: item.diag_stats[varCode].colour || 'grey' }"
                  class="ml-1"
                >
                  {{ formatPercentage(item.diag_stats[varCode].percentage) }}
                </span>
              </template>
              <span
                v-else
                class="grey--text"
              >—</span>
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="text-grey text-caption pa-4">
            {{ variables.selected.length === 0 ? $t('card.diagnostics_latest.select_variables.hint') : $t('table.no_data') }}
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { DiagnosticVariableApiService, PlayerApiService } from '@/data/services'
import { RouteNames } from '@/router/routes'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import keyBy from 'lodash/keyBy'
import draggable from 'vuedraggable'
import injuryIcon from '@/assets/img/player_info/Injury indicator.svg'

export default {
  name: 'DiagnosticLatestTable',
  components: { draggable },
  props: {
    chart: {
      type: Object,
      default: () => ({})
    },
    players: {
      type: Array,
      default: () => []
    },
    player: {
      type: Object,
      default: () => ({})
    },
    teamId: {
      type: Number,
      default: null
    },
    date: {
      type: Object,
      default: () => dayjs()
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    variables: {
      data: [],
      selected: [],
      loading: false
    },
    stats: {
      data: [],
      loading: false
    },
    dateRange: 3,
    variablesMenuVisible: false,
    variablesSearch: '',
    injuryIcon
  }),
  computed: {
    singlePlayer() {
      return !!(this.player && this.player.id)
    },
    playerIds() {
      if (this.singlePlayer) return [this.player.id]
      return this.players.map(p => p.id)
    },
    fromDate() {
      return dayjs(this.date).subtract(this.dateRange, 'month').format('YYYY-MM-DD')
    },
    toDate() {
      return dayjs(this.date).format('YYYY-MM-DD')
    },
    variableByCode() {
      return keyBy(this.variables.data, 'var_id')
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasSmartAssistantModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_smart_assistant_module
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    isIndividualSport() {
      return !!(this.currentTeam && this.currentTeam.sport && this.currentTeam.sport.is_individual)
    },
    headers() {
      const headers = []
      if (!this.singlePlayer) {
        headers.push({ value: '_avatar', text: '', sortable: false, class: 'pr-0', width: '0px' })
        headers.push({ value: 'last_name', text: this.$t('table_headers.name'), width: '200px' })
        if (!this.isIndividualSport) {
          headers.push({ value: '_position', text: this.$t('table_headers.player_overview.position'), sortable: false, width: '150px' })
        }
      }
      for (const varCode of this.variables.selected) {
        const info = this.variableByCode[varCode]
        const label = this.variableLabel(info) || varCode
        const isLong = label.length > 30
        const cls = isLong ? 'diag-th-wrap diag-th-wrap-wide' : 'diag-th-wrap'
        headers.push({ text: label, value: varCode, sortable: false, class: cls })
      }
      return headers
    },
    rows() {
      const byId = new Map()
      for (const s of this.stats.data) byId.set(s.player_id, s.stats || {})
      if (this.singlePlayer) {
        return [{
          ...this.player,
          diag_stats: byId.get(this.player.id) || {}
        }]
      }
      return this.players.map(p => ({
        ...p,
        diag_stats: byId.get(p.id) || {}
      }))
    },
    filteredUnselectedVariables() {
      const selectedSet = new Set(this.variables.selected)
      const unselected = this.variables.data.filter(v => !selectedSet.has(v.var_id))
      if (!this.variablesSearch) return unselected
      const search = this.variablesSearch.toLowerCase()
      return unselected.filter(v => this.variableLabel(v).toLowerCase().includes(search))
    }
  },
  watch: {
    teamId() {
      if (!this.removable) this.fetchMeasuredVariablesDebounced()
    },
    players() {
      if (!this.removable && !this.singlePlayer) this.fetchStatsDebounced()
    },
    date(newVal, oldVal) {
      if (!newVal || !oldVal) return
      if (dayjs(newVal).isSame(dayjs(oldVal), 'day')) return
      if (!this.removable) this.fetchMeasuredVariablesDebounced()
    }
  },
  created() {
    if (this.removable) {
      this.setupMockData()
      return
    }
    if (Array.isArray(this.chart.vars)) {
      this.variables.selected = [...this.chart.vars]
    }
    if (this.chart.period) {
      this.dateRange = this.chart.period
    }
    this.fetchMeasuredVariables()
  },
  methods: {
    variableLabel(v) {
      if (!v) return ''
      return v.test_name ? `${v.var}: ${v.test_name}` : v.var
    },
    unitFor(varCode) {
      const info = this.variableByCode[varCode]
      return info && info.unit ? ` ${info.unit}` : ''
    },
    formatValue(v) {
      if (v === null || v === undefined) return '—'
      return Number.isInteger(v) ? v : Number(v).toFixed(1)
    },
    formatPercentage(p) {
      if (p === null || p === undefined) return ''
      const sign = p > 0 ? '+' : ''
      return `${sign}${Number(p).toFixed(1)}%`
    },
    arrowIcon(stat) {
      const latest = stat && stat.latest
      const previous = stat && stat.previous
      if (latest === null || latest === undefined || previous === null || previous === undefined) return 'fal fa-minus'
      if (latest > previous) return 'fal fa-arrow-trend-up'
      if (latest < previous) return 'fal fa-arrow-trend-down'
      return 'fal fa-minus'
    },
    setupMockData() {
      this.variables.data = [
        { var_id: 'BCPBCPBW', var: 'Body weight', unit: 'kg', test_name: null },
        { var_id: 'ALLDAL', var: 'Arm length', unit: 'cm', test_name: null }
      ]
      this.variables.selected = ['BCPBCPBW', 'ALLDAL']
      this.stats.data = []
    },
    onRowClick(item) {
      if (this.singlePlayer || this.removable || !item || !item.id) return
      this.$router.push({
        name: RouteNames.PLAYER_OVERVIEW,
        params: { id: item.id },
        query: { date: dayjs(this.date).format('YYYY-MM-DD'), route: 'OVERVIEW' }
      })
    },
    navigateToSmartAssistant(item) {
      if (!item || !item.id) return
      this.$router.push({ name: RouteNames.PLAYER_SMART_ASSISTANT, params: { id: item.id } })
    },
    canSeeMedicalIndicator(item) {
      const teamIds = (item && item.team_ids) || []
      return this.$can('view-medical', teamIds) || this.$can('view-basic-medical', teamIds)
    },
    emitSettings() {
      this.$emit('update:chart', {
        ...this.chart,
        vars: [...this.variables.selected],
        period: this.dateRange
      })
    },
    onPeriodChanged() {
      this.emitSettings()
      this.fetchMeasuredVariablesDebounced()
    },
    onVariablesReordered() {
      this.emitSettings()
      // No data refetch — same vars, just different order
    },
    addVariable(varId) {
      if (this.variables.selected.includes(varId)) return
      this.variables.selected.push(varId)
      this.emitSettings()
      this.fetchStatsDebounced()
    },
    removeVariable(varId) {
      const idx = this.variables.selected.indexOf(varId)
      if (idx === -1) return
      this.variables.selected.splice(idx, 1)
      this.emitSettings()
      this.fetchStatsDebounced()
    },
    fetchMeasuredVariablesDebounced: debounce(function () {
      this.fetchMeasuredVariables()
    }, 200),
    fetchStatsDebounced: debounce(function () {
      this.fetchStats()
    }, 200),
    async fetchMeasuredVariables() {
      const params = {
        event_at_date_gte: this.fromDate,
        event_at_date_lte: this.toDate
      }
      if (this.teamId) {
        params.team_id = this.teamId
      } else if (this.singlePlayer) {
        params.player_ids = [this.player.id]
      } else if (this.playerIds.length) {
        params.player_ids = this.playerIds
      } else {
        return
      }
      this.variables.loading = true
      try {
        const response = await DiagnosticVariableApiService.getMeasured(params)
        this.variables.data = response.data.variables || []
        if (this.variables.selected.length === 0 && this.variables.data.length > 0) {
          this.variables.selected = this.variables.data.slice(0, 5).map(v => v.var_id)
        }
      } finally {
        this.variables.loading = false
      }
      this.fetchStats()
    },
    async fetchStats() {
      if (this.variables.selected.length === 0) {
        this.stats.data = []
        return
      }
      if (!this.playerIds.length) return
      this.stats.loading = true
      try {
        const raw = await PlayerApiService.getDailyStats({
          player_ids: this.playerIds,
          from: this.fromDate,
          to: this.toDate,
          stats_types_variables: { diagnostics: [...this.variables.selected] },
          latest: true,
          colours: true
        })
        this.stats.data = Array.isArray(raw) ? raw : (raw && raw.data) || []
      } finally {
        this.stats.loading = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep th.diag-th-wrap {
  white-space: normal !important;
  word-break: break-word;
  max-width: 290px;
  min-width: 120px;
  vertical-align: middle;
  line-height: 1.2;
}
::v-deep th.diag-th-wrap-wide {
  min-width: 230px;
}
</style>
